package sharedlayers

import apiclient.FormationClient
import apiclient.geoobjects.MarkerColor
import apiclient.geoobjects.MarkerIcon
import apiclient.geoobjects.MarkerShape
import apiclient.groups.*
import apiclient.search.MapLayerResults
import auth.CurrentWorkspaceStore
import com.tryformation.localization.Translatable
import dev.fritz2.core.RenderContext
import dev.fritz2.core.placeholder
import dev.fritz2.core.storeOf
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import koin.withKoin
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filterNotNull
import localization.TL
import localization.getTranslationFlow
import localization.translate
import overlays.withBusyApiClient
import theme.FormationIcons
import theme.FormationUIIcons
import twcomponents.*
import kotlin.time.Duration.Companion.milliseconds

enum class LayerSubscriptionTexts : Translatable {
    Title,
    NoLayersAdded,
    AddLayers,
    SearchFieldPlaceholder,
    AllLayersAdded,
    DidNotMatch,
    ;

    override val prefix = "layersubscriptionstexts"
}

fun RenderContext.sharedLayerPicker() {

    withKoin {
        val workspaceStore = get<CurrentWorkspaceStore>()
        val client = get<FormationClient>()
        val mapLayerResultsStore = storeOf<MapLayerResults?>(null)
        val searchStore = storeOf("")

        suspend fun searchLayers(query: String, excludeIds: List<String>) {
            client.searchSharedLayers(query, excludeIds = excludeIds).fold(
                { mapLayerResult ->
                    mapLayerResultsStore.update(mapLayerResult)
                },
                { error ->
                    console.error(error)
                    mapLayerResultsStore.update(null)
                },
            )
        }

        twColOf("gap-3") {

            h3 {
                translate(LayerSubscriptionTexts.Title)
            }

            workspaceStore.data.filterNotNull().render { group ->
                // show enabled layers with toggle and remove option

                val sharedMapLayerDefinitions = group.sharedLayerDefinitions
                if (sharedMapLayerDefinitions.isNullOrEmpty()) {
                    p("px-2 font-light") {
                        translate(LayerSubscriptionTexts.NoLayersAdded)
                    }
                } else {
                    sharedMapLayerDefinitions.forEach { sharedLayer ->
                        twRowOfJustifyBetween("w-full pl-4") {
                            twRowOfJustifyStart {
                                twIconMedium(icon = FormationUIIcons.TwoLayers)
                                p("w-full px-2 rounded-xl font-bold") {
                                    +(sharedLayer.title ?: sharedLayer.layerId)
                                }
                            }
                            twMediumIconButtonDangerRounded(icon = FormationIcons.DeleteAlt) {
                                clicks handledBy {
                                    withBusyApiClient(
                                        {
                                            client.restDeleteLayer(workspaceStore.groupId, sharedLayer.layerId)
                                        },
                                    ) { group ->
                                        searchLayers(
                                            query = searchStore.current,
                                            excludeIds = group.sharedLayerDefinitions.orEmpty()
                                                .map { it.layerId } - sharedLayer.layerId,
                                        )
                                        workspaceStore.update(group)
                                    }
                                }
                            }.twSimpleTextTooltip(TL.General.REMOVE, PlacementValues.bottomEnd)
                        }
                    }
                }
            }

            h3 {
                translate(LayerSubscriptionTexts.AddLayers)
            }

            twInputField(searchStore) {
                twLeftAlignedRow {
                    twIconMedium(icon = FormationIcons.Search)
                }
                twInputTextField {
                    placeholder(LayerSubscriptionTexts.SearchFieldPlaceholder.getTranslationFlow())
                }
                twRightAlignedButtonRow {
                    searchStore.data.render { text ->
                        if (text.isNotBlank()) {
                            twRevertButton(searchStore, "")
                        }
                    }
                }
            }

            searchStore.data.debounce(200.milliseconds).handledBy { query ->
                searchLayers(query, workspaceStore.current?.sharedLayerDefinitions.orEmpty().map { it.layerId })
            }

            mapLayerResultsStore.data.render { results ->
                val hits = results?.hits.orEmpty()
                if (hits.isEmpty()) {
                    div("px-2") {
                        searchStore.data.render { query ->
                            workspaceStore.data.render { group ->
                                if (!group?.sharedLayerDefinitions.isNullOrEmpty() && query.isBlank()) {
                                    p("px-2 font-light") {
                                        translate(LayerSubscriptionTexts.AllLayersAdded)
                                    }
                                } else {
                                    p("px-2 font-light") {
                                        translate(LayerSubscriptionTexts.DidNotMatch, mapOf("query" to query))
                                    }
                                }
                            }
                        }
                    }
                } else {
                    div("flex flex-col gap-2 w-full") {
                        hits.forEach { mapLayerResult ->
                            twRowOfJustifyBetween("w-full pl-4") {
                                twRowOfJustifyStart {
                                    twIconMedium(icon = FormationUIIcons.TwoLayers)
                                    twColOfNoGap("items-start justify-center") {
                                        p("w-full px-2 rounded-xl font-bold") {
                                            +mapLayerResult.name
                                        }
                                        p("w-full px-2 rounded-xl text-sm font-light") {
                                            +("(${mapLayerResult.id})")
                                        }
                                    }

                                }
                                twMediumIconButtonNeutralRounded(icon = FormationIcons.Add) {
                                    clicks handledBy {
                                        withBusyApiClient(
                                            {
                                                client.updateOrCreateLayer(
                                                    workspaceStore.groupId,
                                                    LayerUpdate(
                                                        SharedMapLayerDefinition(
                                                            mapLayerResult.id,
                                                            mapLayerResult.name
                                                        ),
                                                        LayerMetaDataUpdate(
                                                            title = mapLayerResult.name,
                                                            layerVisibility = LayerVisibility.Group,
                                                            layerCacheSettings = null,
                                                            defaultOn = true,
                                                            defaultColor = MarkerColor.Default,
                                                            defaultShape = MarkerShape.Default,
                                                            defaultIconCategory = MarkerIcon.Default,
                                                        ),
                                                    ),
                                                )
                                            },
                                        ) { updatedGroup ->
                                            workspaceStore.update(updatedGroup)
                                            mapLayerResultsStore.update(
                                                mapLayerResultsStore.current?.let { old ->
                                                    old.copy(hits = old.hits.filter { h -> h.id != mapLayerResult.id })
                                                },
                                            )
                                        }
                                    }
                                }.twSimpleTextTooltip(TL.CardMapLayers.ADD_LAYER, PlacementValues.bottomEnd)
                            }
                        }
                    }
                }
            }
        }
    }
}
